import { createContext, type RefObject, type KeyboardEventHandler } from "react";

export const PromptAssistanceAnnotation = createContext<{
  active: boolean;
  editorRef: RefObject<HTMLDivElement | null>;
  descriptionId: string;
  onKeyDown: KeyboardEventHandler<HTMLDivElement>;
} | null>(null);

export const PROMPT_ASSISTANCE_CONTROL_SELECTOR =
  'a, button, input, textarea, select, [role="button"], [data-composer-terminal-context]';

/** A frame count can mistake slow animation or an early pause for a settled anchor. */
export function createPromptAnchorStabilityCheck() {
  let origin: { x: number; y: number; at: number } | null = null;
  return (position: { readonly x: number; readonly y: number }, at: number): boolean => {
    if (
      !origin ||
      Math.abs(position.x - origin.x) >= 0.5 ||
      Math.abs(position.y - origin.y) >= 0.5
    ) {
      origin = { x: position.x, y: position.y, at };
      return false;
    }
    // Compare against the beginning of the whole quiet interval so small
    // movements cannot accumulate unnoticed on high-refresh-rate displays.
    return at - origin.at >= 80;
  };
}

/** Keep the clicked text position anchored as focus expands or scrolls the editor. */
export function createPromptAssistanceAnchor(root: HTMLElement, point?: { x: number; y: number }) {
  const bounds = root.getBoundingClientRect();
  const selection = root.ownerDocument.getSelection();
  let caret: DOMRect | null = null;
  if (!point && selection?.focusNode && root.contains(selection.focusNode)) {
    const range = root.ownerDocument.createRange();
    range.setStart(selection.focusNode, selection.focusOffset);
    range.collapse(true);
    caret = range.getBoundingClientRect();
  }
  const x = (point?.x ?? (caret?.height ? caret.left : bounds.left + 16)) - bounds.left;
  const y =
    (point?.y ?? (caret?.height ? caret.top + caret.height / 2 : bounds.top + 10)) -
    bounds.top +
    root.scrollTop;
  return {
    // A descendant makes the scrollable editor an observed ancestor of the anchor.
    contextElement: root.firstElementChild ?? root,
    getBoundingClientRect: () => {
      const current = root.getBoundingClientRect();
      return DOMRect.fromRect({
        x: current.left + Math.min(x, current.width),
        y: current.top + y - root.scrollTop,
        width: 0,
        height: 0,
      });
    },
  };
}

/** Hit-test draft text, leaving inline controls and blank space to their normal interactions. */
export function isOverPromptText(root: HTMLElement, x: number, y: number): boolean {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const range = document.createRange();
  let node: Node | null;
  while ((node = walker.nextNode())) {
    if (node.parentElement?.closest(PROMPT_ASSISTANCE_CONTROL_SELECTOR)) continue;
    range.selectNodeContents(node);
    for (const rect of range.getClientRects()) {
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) return true;
    }
  }
  return false;
}
